<template>
  <div id="wizard-filter-container" class="mdc-card">
    <section class="mdc-card__primary">
      <form
        id="presentlevelsForm"
        class="form-inline"
        onsubmit="return false;">
        <div class="form-group form-group-lg">
          <label for="studentNameInput" class="prompt">
            <span>I'm developing present levels for </span> <br>
            <input
              type="text"
              id="studentNameInput"
              class="form-control fs-block"
              placeholder="my student's name"
              v-model="studentName">
          </label>
        </div>

        <div class="form-group form-group-lg">
          <label for="gradeSelectInput" class="prompt">
            <span>who is enrolled in</span> <br>
            <select
              id="gradeSelectInput"
              class="form-control"
              :class="{ ' form-control-muted': muteGrade }"
              v-model="selectedGrade">
              <!-- If you change the text of this first option, make sure to also change the value of DEFAULT_STUDENT_GRADE -->
              <option selected disabled>select grade level</option>
              <option
                v-for="grade in enrolledGrades"
                :key="grade.val"
                :value="grade.val">{{ grade.name }}</option>
            </select>
          </label>
        </div>

        <fieldset class="form-group-lg">
          <legend class="prompt">and has areas of strengths/needs in</legend>

          <div class="row">
            <div class="col-sm-6">
              <PresentLevelsSubjectCheckboxWrapper
                v-for="area in areasOfNeed.slice(0, columnBreakSubjectIndex)"
                :key="area.value"
                :label="area.label"
                :date-added="area.dateAdded"
                :disabled="area.disabled"
                :input-id="formatInputId(area.label)"
                v-show="!area.disabled">
                <input
                  type="checkbox"
                  class="mdc-checkbox__native-control"
                  v-model="selectedSubjects"
                  :value="area.value"
                  :disabled="area.disabled"
                  :id="formatInputId(area.label)"
                >
              </PresentLevelsSubjectCheckboxWrapper>
            </div>
            <div class="col-sm-6">
              <PresentLevelsSubjectCheckboxWrapper
                v-for="area in areasOfNeed.slice(columnBreakSubjectIndex)"
                :key="area.value"
                :label="area.label"
                :date-added="area.dateAdded"
                :disabled="area.disabled"
                :input-id="formatInputId(area.label)"
                v-show="!area.disabled">
                <input
                  type="checkbox"
                  class="mdc-checkbox__native-control"
                  v-model="selectedSubjects"
                  :value="area.value"
                  :disabled="area.disabled"
                  :id="formatInputId(area.label)"
                >
              </PresentLevelsSubjectCheckboxWrapper>
            </div>
          </div>

        </fieldset>

        <div class="form-group text-center">
          <button
            :disabled="savingPresentLevels"
            v-if="submittableForm"
            type="button"
            form="presentlevelsForm"
            id="plActionButton"
            class="btn btn-primary btn-lg"
            @click="developPresentLevels">
            <i v-if="savingPresentLevels" class="fa fa-circle-o-notch fa-spin"/> Develop present levels <br>
          </button>
        </div>
      </form>
    </section>
  </div>
</template>

<script>
import {
  getAreasOfNeed,
  getEnrolledGradesSet,
  getPresentLevelStudentStatement,
  postPresentLevelStudent
} from '@/api/presentlevels'
import { authorizationCheckMixin } from '@/router/authorization_mixin'
import { blockPlwMixin } from '@/components/presentlevels/block_plw_mixin'
import PresentLevelsSubjectCheckboxWrapper from './PresentLevelsSubjectCheckboxWrapper'
import { mapMutations, mapState, mapGetters } from 'vuex'
import { htmlIDFromString  } from '@/util/html_utils'

const DEFAULT_STUDENT_GRADE = 'select grade level'

export default {
  name: 'PresentlevelsWizardFilters',
  components: {
    PresentLevelsSubjectCheckboxWrapper
  },
  mixins: [authorizationCheckMixin, blockPlwMixin],
  props: {
    uuid: {
      type: String,
      default: undefined
    }
  },
  data() {
    return {
      studentName: '',
      selectedGrade: DEFAULT_STUDENT_GRADE,
      selectedSubjects: [],
      studentSelections: [],

      savingPresentLevels: false,
      columnBreakSubjectIndex: 7 // this number is tied to the list of subjects and will likely need to change as subjects are added/re-ordered
    }
  },
  async created() {
    let q = new Promise(resolve => {
      resolve()
    })

    if (this.uuid) {
      try {
        q = getPresentLevelStudentStatement(this.uuid)
        let data = await q
        await this.redirectIfUnauthorized(data.student.user_uuid, {
          name: 'PresentLevelsNewWizard'
        })

        this.studentName = data.student.student_name
        this.selectedGrade = data.student.enrolled_grade_level
        this.selectedSubjects = data.student.selected_subjects
        this.setLastUpdate(new Date())
        this.setBreadcrumbInfo({
          uuid: this.uuid,
          availableStages: this.selectedSubjects
        })
        this.setEnrolledGrade(this.selectedGrade)
        this.setStudentName(this.studentName)
        this.setAreasOfNeed(this.selectedSubjects)
      } catch (err) {
        this.$router.replace({ name: 'PresentLevelsNewWizard' })
        console.error(err)
      }
    } else {
      this.resetState()
    }
  },
  computed: {
    ...mapGetters({
      beta: 'visibility/betaVisibility',
      selVisibility: 'visibility/selVisibility',
      effectsVisibility: 'visibility/effectsVisibility'
    }),
    ...mapState({
      userUuid: state => state.user.user.uuid
    }),
    areasOfNeed() {
      let areas = getAreasOfNeed({selBlocked: !this.selVisibility, blockedSubjects: this.effectsVisibility ? ['Behavior'] : []})
      if (this.beta) {
        return areas
      }
      return areas.filter(area => !area.beta)
    },
    enrolledGrades() {
      return getEnrolledGradesSet()
    },
    submittableForm() {
      return (
        this.studentName !== '' &&
        this.selectedGrade !== DEFAULT_STUDENT_GRADE &&
        this.selectedSubjects.length > 0
      )
    },
    muteGrade() {
      return this.selectedGrade === DEFAULT_STUDENT_GRADE
    }
  },
  methods: {
    ...mapMutations('presentlevels', [
      'resetState',
      'setBreadcrumbInfo',
      'setLastUpdate',
      'setEnrolledGrade',
      'setStudentName',
      'setAreasOfNeed'
    ]),
    developPresentLevels() {
      this.$sendEvent('Clicked Develop Present Levels', {
        enrolled_grade: this.selectedGrade,
        areas_of_need: this.selectedSubjects
      })
      if (this.shouldBlockPLW()) {
        return
      }
      this.setEnrolledGrade(this.selectedGrade)
      this.setStudentName(this.studentName)
      let sortedSelectedSubjects = this.areasOfNeed
        .filter(elem => this.selectedSubjects.includes(elem.value))
        .map(elem => elem.value)
      this.selectedSubjects = sortedSelectedSubjects
      this.setAreasOfNeed(this.selectedSubjects)
      this.savingPresentLevels = true
      postPresentLevelStudent({
        uuid: this.uuid,
        name: this.stripHTMLTags(this.studentName),
        pronoun: '',
        enrolledGrade: this.selectedGrade,
        subjects: this.selectedSubjects,
        userUuid: this.userUuid
      })
        .then(data => {
          if (data.student.uuid) {
            this.savingPresentLevels = false
            this.setLastUpdate(new Date())
            if (this.$route.params.uuid !== data.student.uuid) {
              // if the user specified in the route hasn't been set
              this.$router.replace({
                name: 'PresentLevelsExistingWizard',
                params: { uuid: data.student.uuid }
              }).then(() => {
                this.$router.push({
                  name: 'PresentlevelsSubjectResults',
                  params: { uuid: data.student.uuid, subject: data.student.selected_subjects[0] }
                })
              })
            } else {
              // user was already set, just move to results
              this.$router.push({
                name: 'PresentlevelsSubjectResults',
                params: { uuid: data.student.uuid, subject: data.student.selected_subjects[0] }
              })
            }
          } else {
            this.$router.push({ name: 'PresentlevelsBrowse' })
          }
        })
        .catch(error => {
          console.error(error)
        })
    },
    formatInputId(string) {
      return htmlIDFromString(string)
    },
    stripHTMLTags(string) {
      if (string === null || string === '') return ''
      else string = string.toString()
      return string.replace(/(<([^>]+)>)/gi, '')
    },
  },
}
</script>

<style scoped lang="scss">
@import '@material/card/mdc-card';

#wizard-filter-container {
  background: #d6f0f7;
  border-radius: 10px;
  padding: 15px;

  .prompt {
    font-size: 32px;
    font-style: italic;
    line-height: 60px;
    font-weight: normal;
  }

  .form-group {
    margin-top: 20px;

    .form-control-muted {
      // default bootstrap placeholder color, darkened for accessibility
      color: darken(#999, 20%);
    }

    #studentNameInput {
      height: 62px;
      padding: 10px;
      line-height: 26px;
      font-size: 32px;
      width: 500px;
      font-style: normal;
      &:focus {
        border-color: var(--blue-default);
      }
    }

    #gradeSelectInput {
      height: 62px;
      line-height: 50px;
      font-size: 32px;
      font-style: normal;
      width: 500px;
      &:focus {
        border-color: var(--blue-default);
      }
    }

    #plActionButton {
      margin-top: 20px;
      padding: 15px 25px;
      font-size: 26px;
    }
  }

  .form-group-lg {
    margin-top: 20px;
  }
  legend {
    border-bottom: none;
    margin-bottom: 0px;
  }
}
</style>
